import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, SignInButton, UserButton } from '@clerk/clerk-react';
import { Button } from './ui/button';
import { Settings } from 'lucide-react';

const AuthButton = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const [redirectUrl, setRedirectUrl] = useState();
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const pollingRef = useRef(null);
  const isSignedInRef = useRef(isSignedIn);
  
  // Keep ref updated with latest isSignedIn value
  useEffect(() => {
    isSignedInRef.current = isSignedIn;
  }, [isSignedIn]);
  
  // Immediately stop polling and complete auth check if isSignedIn becomes true
  useEffect(() => {
    if (isLoaded && isSignedIn && !authCheckComplete) {
      // console.log('[AuthButton] ✅ isSignedIn changed to true - stopping polling immediately');
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
      setAuthCheckComplete(true);
    }
  }, [isLoaded, isSignedIn, authCheckComplete]);
  
  // console.log('[AuthButton] isSignedIn:', isSignedIn, 'isLoaded:', isLoaded);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setRedirectUrl(window.location.href);
    }
  }, []);

  // Wait for Clerk to sync auth state after cross-domain redirect
  // Use ref to always check latest isSignedIn value, not stale closure value
  useEffect(() => {
    if (!isLoaded) {
      setAuthCheckComplete(false);
      return;
    }

    // Clear any existing polling
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }

    // Check for redirect params or cross-domain referrer
    const hasRedirectParams = 
      window.location.search.includes('__clerk') || 
      window.location.hash.includes('__clerk');
    
    // Also check if referrer is from different domain
    let hasCrossDomainReferrer = false;
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        hasCrossDomainReferrer = referrerUrl.hostname !== window.location.hostname;
      } catch (e) {
        // Invalid referrer URL, ignore
      }
    }
    
    const isFromRedirect = hasRedirectParams || hasCrossDomainReferrer;
    
    if (isFromRedirect) {
      // Coming from redirect - poll for auth state changes
      // Use longer polling time since Clerk needs to sync cross-domain
      let checkCount = 0;
      const maxChecks = 60; // 6 seconds total (60 * 100ms)
      
      const pollAuth = () => {
        checkCount++;
        
        // Always check the latest value from ref, not stale closure
        const currentIsSignedIn = isSignedInRef.current;
        
        // console.log(`[AuthButton] Poll ${checkCount}/${maxChecks}: isSignedIn=${currentIsSignedIn}, isLoaded=${isLoaded}`);
        
        // If user is signed in, we're done
        if (currentIsSignedIn) {
          // console.log('[AuthButton] ✅ User authenticated after', checkCount, 'checks');
          setAuthCheckComplete(true);
          pollingRef.current = null;
          return;
        }
        
        // If we've checked enough times, give up
        if (checkCount >= maxChecks) {
          // console.log('[AuthButton] ⏰ Timeout after', maxChecks, 'checks - showing sign in');
          setAuthCheckComplete(true);
          pollingRef.current = null;
          return;
        }
        
        // Check again in 100ms
        pollingRef.current = setTimeout(pollAuth, 100);
      };
      
      // Start polling after initial delay to allow Clerk to sync
      pollingRef.current = setTimeout(pollAuth, 500);
    } else {
      // Normal page load - quick check
      pollingRef.current = setTimeout(() => {
        setAuthCheckComplete(true);
        pollingRef.current = null;
      }, 150);
    }

    // Cleanup
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isLoaded]); // Only depend on isLoaded, not isSignedIn (we use ref for that)

  // Show loading state while Clerk is loading OR during grace period
  if (!isLoaded || !authCheckComplete) {
    return (
      <div className="flex items-center gap-2">
        <div className="animate-spin h-4 w-4 border-2 border-slate-300 border-t-slate-600 rounded-full"></div>
      </div>
    );
  }

  // User is signed in
  if (isSignedIn) {
    return (
      <div className="flex items-center gap-2">
        <UserButton 
          appearance={{
            elements: {
              avatarBox: "h-8 w-8",
              userButtonPopoverCard: "shadow-lg"
            }
          }}
        />
      </div>
    );
  }

  // User is not signed in (after grace period)
  if (redirectUrl) {
    return (
      <SignInButton mode="redirect" forceRedirectUrl={redirectUrl}>
        <Button variant="outline" size="sm">
          Sign In
        </Button>
      </SignInButton>
    );
  }

  // Fallback loading state
  return (
    <div className="flex items-center gap-2">
      <div className="animate-spin h-4 w-4 border-2 border-slate-300 border-t-slate-600 rounded-full"></div>
    </div>
  );
};

export default AuthButton;